import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  Alert,
  Tooltip,
  IconButton,
  alpha,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  categoryAPI,
  Category,
  CreateCategoryData,
  UpdateCategoryData,
} from "../../services/categoryAPI";
import { useRBAC } from "../../hooks/useRBAC";
import { useTradeTerminology } from "../../utils/tradeTerminology";
import PageHeader from "../../components/layout/PageHeader";
import DataListCard from "../../components/layout/DataListCard";
import SortableTableCell from "../../components/layout/SortableTableCell";
import TableActionButton from "../../components/layout/TableActionButton";
import { useTableSort } from "../../hooks/useTableSort";
import { tokens } from "../../theme";

type CategorySortField = "name" | "description" | "parent" | "status" | "createdAt";

const categorySchema = yup.object({
  name: yup.string().required("Name is required"),
  description: yup.string().required("Description is required"),
  parent_category_id: yup.string().nullable().notRequired(),
});

type CategoryFormData = yup.InferType<typeof categorySchema>;
type StatusFilter = "all" | "active" | "inactive";
type ParentFilter = "all" | "top" | string;

type CategoryRow = Category & {
  parentCategoryId?: string | null;
  parent_category_id?: string | null;
  isActive?: boolean;
  is_active?: boolean;
  createdAt?: string;
  created_at?: string;
  parent?: { id: string; name: string };
};

const getParentId = (category: CategoryRow) =>
  category.parent_category_id ?? category.parentCategoryId ?? null;

const getIsActive = (category: CategoryRow) =>
  category.is_active ?? category.isActive ?? true;

const getCreatedAt = (category: CategoryRow) =>
  category.created_at ?? category.createdAt;

const CategoriesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { terms } = useTradeTerminology();
  const { canCreate, canUpdate, canDelete } = useRBAC();
  const { sortBy, sortOrder, handleSort, sortParams } =
    useTableSort<CategorySortField>("name");
  const onSort = (field: CategorySortField) => handleSort(field, () => setPage(0));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState(() => {
    if (typeof window === "undefined") return "";
    return new URLSearchParams(window.location.search).get("search") || "";
  });
  const [debouncedSearch, setDebouncedSearch] = useState(() => {
    if (typeof window === "undefined") return "";
    return (new URLSearchParams(window.location.search).get("search") || "").trim();
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [parentFilter, setParentFilter] = useState<ParentFilter>("all");
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<CategoryRow | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    category: CategoryRow | null;
  }>({ open: false, category: null });
  const [alert, setAlert] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const {
    data: categoriesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      "categories",
      page,
      rowsPerPage,
      debouncedSearch,
      statusFilter,
      parentFilter,
      sortBy,
      sortOrder,
    ],
    queryFn: () =>
      categoryAPI.getCategories({
        pageNo: page + 1,
        pageSize: rowsPerPage,
        ...sortParams,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
        ...(statusFilter === "active" ? { isActive: true } : {}),
        ...(statusFilter === "inactive" ? { isActive: false } : {}),
        ...(parentFilter === "top" ? { topLevel: true } : {}),
        ...(parentFilter !== "all" && parentFilter !== "top"
          ? { parentId: parentFilter }
          : {}),
      }),
  });

  const { data: parentCategories } = useQuery({
    queryKey: ["parent-categories"],
    queryFn: () => categoryAPI.getParentCategories({ is_active: true }),
  });

  const { mutate: createCategory, isPending: isCreating } = useMutation({
    mutationFn: (data: CreateCategoryData) => categoryAPI.createCategory(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["parent-categories"] });
      setAlert({ type: "success", message: "Category created successfully" });
      handleCloseDialog();
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to create category",
      });
    },
  });

  const { mutate: updateCategory, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryData }) =>
      categoryAPI.updateCategory(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["parent-categories"] });
      setAlert({ type: "success", message: "Category updated successfully" });
      handleCloseDialog();
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to update category",
      });
    },
  });

  const { mutate: deleteCategory, isPending: isDeleting } = useMutation({
    mutationFn: (id: string) => categoryAPI.deleteCategory(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categories"] });
      queryClient.invalidateQueries({ queryKey: ["parent-categories"] });
      setAlert({ type: "success", message: "Category deleted successfully" });
      setDeleteDialog({ open: false, category: null });
    },
    onError: (err: any) => {
      setAlert({
        type: "error",
        message: err.response?.data?.message || "Failed to delete category",
      });
    },
  });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CategoryFormData>({
    resolver: yupResolver(categorySchema),
    defaultValues: {
      name: "",
      description: "",
      parent_category_id: null,
    },
  });

  const categories = (categoriesData?.data || []) as CategoryRow[];
  const totalCount = categoriesData?.total || 0;
  const parents = parentCategories || [];

  const resolveParentName = (category: CategoryRow) => {
    const parentId = getParentId(category);
    if (!parentId) return "—";
    return (
      category.parent?.name ||
      parents.find((p) => p.id === parentId)?.name ||
      "—"
    );
  };

  const handleOpenDialog = (category?: CategoryRow) => {
    if (category) {
      setEditingCategory(category);
      reset({
        name: category.name,
        description: category.description || "",
        parent_category_id: getParentId(category) || null,
      });
    } else {
      setEditingCategory(null);
      reset({
        name: "",
        description: "",
        parent_category_id: null,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingCategory(null);
    reset();
  };

  const onSubmit = (data: CategoryFormData) => {
    const processedData = {
      ...data,
      parent_category_id: data.parent_category_id || undefined,
    };

    if (editingCategory) {
      updateCategory({ id: editingCategory.id, data: processedData });
    } else {
      createCategory(processedData);
    }
  };

  const filterDropdowns = (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ width: "100%" }}>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 160 } }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={statusFilter}
          label="Status"
          onChange={(e) => {
            setStatusFilter(e.target.value as StatusFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Status</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </Select>
      </FormControl>
      <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 200 } }}>
        <InputLabel>Parent</InputLabel>
        <Select
          value={parentFilter}
          label="Parent"
          onChange={(e) => {
            setParentFilter(e.target.value as ParentFilter);
            setPage(0);
          }}
        >
          <MenuItem value="all">All Categories</MenuItem>
          <MenuItem value="top">Top Level Only</MenuItem>
          {parents.map((parent) => (
            <MenuItem key={parent.id} value={parent.id}>
              Under: {parent.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        title={terms.categoryPlural}
        subtitle="Organize items with categories and subcategories"
        breadcrumbs={[
          { label: "Dashboard", to: "/dashboard" },
          { label: terms.categoryPlural },
        ]}
        action={
          <>
            <Tooltip title="Export PDF">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <PdfIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export Excel">
              <IconButton size="small" sx={{ border: "1px solid", borderColor: "divider" }}>
                <ExcelIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {canCreate("categories") && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
              >
                {terms.categoryAddButton}
              </Button>
            )}
          </>
        }
      />

      {alert && (
        <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>
          {alert.message}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : "Failed to load categories"}
        </Alert>
      )}

      <DataListCard
        searchPlaceholder="Search name or description…"
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(0);
        }}
        filters={filterDropdowns}
        footer={
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ width: "100%" }}
          />
        }
      >
        {isLoading ? (
          <Box display="flex" justifyContent="center" py={6}>
            <Typography color="text.secondary">Loading categories…</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <TableCell width={52}>S.No.</TableCell>
                  <SortableTableCell label="Category" field="name" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Description" field="description" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Parent" field="parent" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Status" field="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <SortableTableCell label="Created" field="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} />
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((category, index) => {
                  const isActive = getIsActive(category);
                  return (
                    <TableRow key={category.id} hover>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {page * rowsPerPage + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: tokens.radius.sm,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              bgcolor: alpha(tokens.colors.dashBlue, 0.12),
                              color: tokens.colors.dashBlue,
                              flexShrink: 0,
                            }}
                          >
                            <CategoryIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Box minWidth={0}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                              {category.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {getParentId(category) ? "Subcategory" : "Top level"}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: 220 }}
                        >
                          {category.description || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {resolveParentName(category)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isActive ? "Active" : "Inactive"}
                          size="small"
                          color={isActive ? "success" : "default"}
                          variant={isActive ? "filled" : "outlined"}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {getCreatedAt(category)
                            ? format(new Date(getCreatedAt(category)!), "dd MMM yyyy")
                            : "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {canUpdate("categories") && (
                            <TableActionButton
                              title="Edit category"
                              variant="primary"
                              onClick={() => handleOpenDialog(category)}
                            >
                              <EditIcon sx={{ fontSize: 18 }} />
                            </TableActionButton>
                          )}
                          {canDelete("categories") && (
                            <TableActionButton
                              title="Delete category"
                              variant="danger"
                              onClick={() => setDeleteDialog({ open: true, category })}
                            >
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </TableActionButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {categories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Typography color="text.secondary">No categories found</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DataListCard>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle sx={{ fontWeight: 700 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6" fontWeight={700}>
                {editingCategory ? `Edit ${terms.categorySingular}` : terms.categoryAddButton}
              </Typography>
              <IconButton size="small" onClick={handleCloseDialog}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent dividers sx={{ maxHeight: "72vh" }}>
            <Grid container spacing={2} sx={{ pt: 1 }}>
              <Grid item xs={12}>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                      error={!!errors.description}
                      helperText={errors.description?.message}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="parent_category_id"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.parent_category_id}>
                      <InputLabel>Parent category (optional)</InputLabel>
                      <Select
                        {...field}
                        value={field.value ?? ""}
                        label="Parent category (optional)"
                        onChange={(e) => field.onChange(e.target.value || null)}
                      >
                        <MenuItem value="">None (top level)</MenuItem>
                        {parents.map((parent) => (
                          <MenuItem
                            key={parent.id}
                            value={parent.id}
                            disabled={editingCategory?.id === parent.id}
                          >
                            {parent.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.parent_category_id && (
                        <FormHelperText>
                          {errors.parent_category_id.message}
                        </FormHelperText>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog} color="inherit">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isCreating || isUpdating}
            >
              {isCreating || isUpdating
                ? "Saving…"
                : editingCategory
                  ? "Update"
                  : "Create"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, category: null })}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{deleteDialog.category?.name}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, category: null })}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              deleteDialog.category && deleteCategory(deleteDialog.category.id)
            }
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesPage;
